// src/components/pages/CheckoutService.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaArrowLeft, FaCreditCard, FaSpinner, FaMapMarkerAlt,
  FaUser, FaPhone, FaSync
} from 'react-icons/fa';
import { placeOrderAsync, fetchCartItemsAsync } from '../../redux/slices/cartSlice';

const CheckoutService = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme?.mode || 'light');
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { items, totalPrice, totalItems, isLoading } = useSelector((state) => state.cart);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // ✅ Fetch cart items from API when component mounts
    const loadCart = async () => {
      try {
        console.log('🔄 Loading cart items on mount...');
        await dispatch(fetchCartItemsAsync()).unwrap();
        console.log('✅ Cart loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load cart:', error);
      }
    };
    loadCart();

    // Load user data
    const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
    setFormData({
      name: userData.name || '',
      phone: userData.phone || '',
      address: userData.address || ''
    });

    // ✅ Debug: Check what's in localStorage
    const storedCart = localStorage.getItem('cartItems');
    console.log('📦 Stored cart in localStorage:', storedCart);
    console.log('📦 Redux cart items:', items);
    console.log('👤 User data:', userData);

  }, [user, navigate, dispatch]);

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
  };

  const cartTotal = totalPrice || calculateTotal();
  const gst = cartTotal * 0.18;
  const grandTotal = cartTotal + gst;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ✅ Function to refresh cart
  const handleRefreshCart = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Refreshing cart...');
      const result = await dispatch(fetchCartItemsAsync()).unwrap();
      console.log('✅ Cart refreshed:', result);
      // Show success message
      alert('Cart refreshed successfully! Items: ' + (result?.data?.length || 0));
    } catch (error) {
      console.error('❌ Failed to refresh cart:', error);
      alert('Failed to refresh cart. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ✅ Get cart items from multiple sources
  const getCartItems = () => {
    // First try Redux
    let cartItems = items || [];
    console.log('📦 Cart items from Redux:', cartItems);
    
    // If Redux is empty, try localStorage
    if (cartItems.length === 0) {
      try {
        const stored = localStorage.getItem('cartItems');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            cartItems = parsed;
            console.log('📦 Got cart items from localStorage:', cartItems);
          }
        }
      } catch (e) {
        console.warn('Could not parse cart items from localStorage');
      }
    }
    
    // If still empty, try sessionStorage
    if (cartItems.length === 0) {
      try {
        const stored = sessionStorage.getItem('cartItems');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            cartItems = parsed;
            console.log('📦 Got cart items from sessionStorage:', cartItems);
          }
        }
      } catch (e) {
        console.warn('Could not parse cart items from sessionStorage');
      }
    }
    
    return cartItems;
  };

  const handlePlaceOrder = async () => {
    if (!formData.name || !formData.phone || !formData.address) {
      alert('Please fill in all delivery details');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // ✅ First try to refresh cart before placing order
      try {
        console.log('🔄 Refreshing cart before placing order...');
        await dispatch(fetchCartItemsAsync()).unwrap();
        console.log('✅ Cart refreshed before order');
      } catch (refreshError) {
        console.warn('⚠️ Could not refresh cart:', refreshError);
      }
      
      // ✅ Get cart items from Redux state
      let cartItems = items || [];
      console.log('📦 Cart items from Redux before order:', cartItems);
      
      // ✅ If Redux cart is empty, try localStorage
      if (cartItems.length === 0) {
        try {
          const stored = localStorage.getItem('cartItems');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cartItems = parsed;
              console.log('📦 Using cart items from localStorage:', cartItems);
            }
          }
        } catch (e) {
          console.warn('Could not parse cart items from localStorage');
        }
      }
      
      // ✅ If still empty, try sessionStorage
      if (cartItems.length === 0) {
        try {
          const stored = sessionStorage.getItem('cartItems');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cartItems = parsed;
              console.log('📦 Using cart items from sessionStorage:', cartItems);
            }
          }
        } catch (e) {
          console.warn('Could not parse cart items from sessionStorage');
        }
      }
      
      // ✅ Check if cart is empty
      if (!cartItems || cartItems.length === 0) {
        alert('Your cart is empty! Please add items before placing an order.');
        setIsSubmitting(false);
        return;
      }
      
      console.log('📦 Final cart items for order:', cartItems);
      
      // Get user ID
      const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id || 1;
      
      // ✅ Format items for the API
      const formattedItems = cartItems.map(item => ({
        item_id: parseInt(item.item_id) || parseInt(item.id) || 0,
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        item_type: item.item_type || 'product',
        product_name: item.product_name || item.name || 'Item'
      }));
      
      console.log('📦 Formatted items:', formattedItems);
      
      // Calculate totals
      const subtotal = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const gstAmount = subtotal * 0.18;
      const total = subtotal + gstAmount;
      
      // ✅ Prepare complete order data with ALL required fields
      const orderData = {
        user_id: parseInt(userId),
        payment_method: 'cash',
        customer_address: formData.address,
        customer_phone: formData.phone,
        customer_name: formData.name,
        items: formattedItems,        // ✅ CRITICAL: Include cart items
        subtotal: subtotal,
        gst: gstAmount,
        total: total,
        delivery_charge: 0,
        latitude: 22.5726,
        longitude: 88.3639
      };
      
      console.log('📤 Placing order with FULL data:', JSON.stringify(orderData, null, 2));
      console.log('📤 Items being sent:', formattedItems.length);
      
      // ✅ Use placeOrderAsync which calls /order/place endpoint
      const result = await dispatch(placeOrderAsync(orderData)).unwrap();
      console.log('✅ Order placed:', result);
      
      // Save address to profile
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.address = formData.address;
      userData.name = formData.name;
      userData.phone = formData.phone;
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Clear cart after successful order
      localStorage.removeItem('cartItems');
      sessionStorage.removeItem('cartItems');
      
      // Navigate to success page
      const orderResult = result?.data || result;
      navigate('/order-success', { 
        state: { 
          orderId: orderResult?.order_id,
          orderNumber: orderResult?.order_number,
          totalAmount: orderResult?.total_amount || total
        } 
      });
      
    } catch (error) {
      console.error('❌ Order failed:', error);
      
      // ✅ Show detailed error message
      let errorMsg = 'Failed to place order. Please try again.';
      if (typeof error === 'string') {
        errorMsg = error;
      } else if (error?.message) {
        errorMsg = error.message;
      } else if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorMsg = Object.values(errors).flat().join(', ');
      }
      
      alert('❌ ' + errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated && !localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <p className="text-lg" style={{ color: colors.text }}>Please login to checkout</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 rounded-full text-white"
            style={{ backgroundColor: colors.primary }}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <div className="relative pt-8 pb-6 px-4">
        <div className="container mx-auto">
          <button
            onClick={() => navigate('/view-cart')}
            className="flex items-center gap-2 mb-4 px-4 py-2 rounded-full transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              color: colors.text
            }}
          >
            <FaArrowLeft />
            <span>Back to Cart</span>
          </button>
          
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            Checkout
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl p-6" style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
                <FaMapMarkerAlt style={{ color: colors.primary }} />
                Delivery Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-xl focus:outline-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-xl focus:outline-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Delivery Address *
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-3 rounded-xl focus:outline-none resize-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your delivery address"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-xl p-6 sticky top-4" style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold" style={{ color: colors.text }}>
                  Order Summary
                </h3>
                <button
                  onClick={handleRefreshCart}
                  disabled={isRefreshing}
                  className="text-sm px-3 py-1.5 rounded-lg transition-all hover:scale-105 flex items-center gap-2"
                  style={{ 
                    backgroundColor: colors.primary,
                    color: 'white',
                    opacity: isRefreshing ? 0.6 : 1
                  }}
                >
                  <FaSync className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-4" style={{ color: colors.textSecondary }}>
                    Loading cart items...
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-4" style={{ color: colors.textSecondary }}>
                    <p>Your cart is empty</p>
                    <button
                      onClick={() => navigate('/products')}
                      className="mt-2 text-sm underline"
                      style={{ color: colors.primary }}
                    >
                      Browse Products
                    </button>
                  </div>
                ) : (
                  items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                      <span>{item.product_name || 'Item'} × {item.quantity}</span>
                      <span>₹{(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
              
              <div className="border-t pt-3 mt-3 space-y-2" style={{ borderColor: colors.border }}>
                <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                  <span>Subtotal ({totalItems || items.length} items)</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                  <span>GST (18%)</span>
                  <span>₹{gst.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 mt-2" style={{ borderColor: colors.border }}>
                  <div className="flex justify-between">
                    <span className="font-bold" style={{ color: colors.text }}>Total</span>
                    <span className="text-xl font-bold" style={{ color: colors.primary }}>
                      ₹{grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={isSubmitting || items.length === 0}
                className="w-full mt-6 py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: items.length === 0 ? colors.textSecondary : colors.primary,
                  boxShadow: items.length === 0 ? 'none' : `0 4px 20px ${colors.primary}40`
                }}
              >
                {isSubmitting ? (
                  <><FaSpinner className="animate-spin" /> Placing Order...</>
                ) : (
                  <><FaCreditCard /> Place Order</>
                )}
              </button>
              
              {items.length === 0 && (
                <p className="text-center text-xs mt-2" style={{ color: colors.textSecondary }}>
                  Add items to your cart to place an order
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutService;